import { useCallback, useMemo, useRef } from 'react'
import type { FocusEvent, ReactNode } from 'react'

import { cn } from 'components/UI/cn'

import { useDataTableSpreadsheet } from './DataTableSpreadsheetContext'
import { isTargetInsideSpreadsheetRoot } from './spreadsheetDom'
import {
  cellKey,
  getOptimisticSavingValue,
  getSpreadsheetCellPreviewValue,
  shouldClearSelectionOnKey,
} from './spreadsheetSelection'
import type {
  SpreadsheetCellCoordinate,
  SpreadsheetEditableMeta,
} from './spreadsheetTypes'

interface Props<TRow> {
  row: TRow
  rowId: string
  rowIndex: number
  columnId: string
  editable: SpreadsheetEditableMeta<TRow>
  children: ReactNode
}

export function DataTableSpreadsheetCell<TRow>({
  row,
  rowId,
  rowIndex,
  columnId,
  editable,
  children,
}: Props<TRow>) {
  const cellRef = useRef<HTMLDivElement>(null)
  const skipNextFocusSelectRef = useRef(false)
  const skipNextBlurCommitRef = useRef(false)
  const spreadsheet = useDataTableSpreadsheet<TRow>()
  const coordinate: SpreadsheetCellCoordinate = useMemo(
    () => ({ rowIndex, rowId, columnId }),
    [columnId, rowId, rowIndex]
  )
  const key = cellKey(coordinate)
  const canEdit = editable.canEdit?.(row) ?? true
  const isSelected = canEdit && Boolean(spreadsheet?.isCellSelected(coordinate))
  const isActive =
    canEdit &&
    spreadsheet?.activeCell?.rowId === rowId &&
    spreadsheet.activeCell.columnId === columnId
  const saveState = spreadsheet?.saveStates[key]
  const optimisticSavingValue = getOptimisticSavingValue(saveState)
  const isEditing = isActive && spreadsheet?.editing
  const previewValue = getSpreadsheetCellPreviewValue({
    editing: Boolean(spreadsheet?.editing),
    selected: isSelected,
    draftValue: spreadsheet?.draftValue ?? '',
    optimisticSavingValue,
  })
  const selectDraftOnEdit = spreadsheet?.selectDraftOnEdit ?? true
  const registerCell = spreadsheet?.registerCell
  const unregisterCell = spreadsheet?.unregisterCell

  const setCellNode = useCallback(
    (node: HTMLDivElement | null) => {
      cellRef.current = node
      if (!canEdit || !registerCell || !unregisterCell) return

      if (node) {
        registerCell({ coordinate, row, meta: editable })
      } else {
        unregisterCell(coordinate)
      }
    },
    [canEdit, coordinate, editable, registerCell, row, unregisterCell]
  )

  const setInputNode = useCallback(
    (node: HTMLInputElement | null) => {
      if (!node) return

      node.focus()
      if (selectDraftOnEdit) {
        node.select()
        return
      }

      const cursorPosition = node.value.length
      node.setSelectionRange(cursorPosition, cursorPosition)
    },
    [selectDraftOnEdit]
  )

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false
      return
    }

    // Don't re-focus the (old) active cell on a blur commit — focus is moving
    // elsewhere (e.g. the cell the user just clicked), and re-focusing would
    // steal it back, requiring a second click before typing registers.
    void spreadsheet?.commitDraft({ refocus: false })
    if (!isTargetInsideSpreadsheetRoot(event.relatedTarget)) {
      spreadsheet?.clearActiveCell()
    }
  }

  if (!spreadsheet || !canEdit) {
    return <>{children}</>
  }

  return (
    <div
      ref={setCellNode}
      role="gridcell"
      tabIndex={0}
      data-spreadsheet-cell="true"
      data-spreadsheet-row-id={rowId}
      data-spreadsheet-column-id={columnId}
      data-spreadsheet-active={isActive || undefined}
      data-spreadsheet-selected={isSelected || undefined}
      className={cn(
        'absolute inset-0 box-border flex min-w-0 cursor-cell items-center px-6 outline-none',
        'hover:ring-1 hover:ring-inset hover:ring-brand/30',
        isSelected && 'bg-brand/5 ring-1 ring-inset ring-brand/30',
        isActive && 'z-10 ring-2 ring-inset ring-brand',
        saveState?.status === 'saving' && 'opacity-70',
        saveState?.status === 'error' && 'z-10 ring-2 ring-inset ring-error'
      )}
      title={saveState?.status === 'error' ? saveState.error : undefined}
      onMouseDown={(event) => {
        event.stopPropagation()
        event.preventDefault()

        if (document.activeElement !== event.currentTarget) {
          skipNextFocusSelectRef.current = true
          event.currentTarget.focus()
        }

        if (event.shiftKey) {
          spreadsheet.selectCell(coordinate, true)
          spreadsheet.endDrag()
          return
        }

        spreadsheet.startDrag(coordinate)
      }}
      onMouseEnter={(event) => {
        event.stopPropagation()
        spreadsheet.extendDrag(coordinate)
      }}
      onMouseUp={(event) => {
        event.stopPropagation()
        spreadsheet.endDrag()
      }}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onFocus={(event) => {
        if (skipNextFocusSelectRef.current) {
          skipNextFocusSelectRef.current = false
          return
        }

        if (event.target !== event.currentTarget || isActive) return
        spreadsheet.selectCell(coordinate, false)
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return

        if (
          (event.metaKey || event.ctrlKey) &&
          event.key.toLowerCase() === 'c'
        ) {
          event.preventDefault()
          void spreadsheet.copySelection()
          return
        }

        if (event.key === 'Enter') {
          event.preventDefault()
          spreadsheet.beginEdit(editable.getValue(row))
          return
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault()
          spreadsheet.moveActiveCell(1, 0, event.shiftKey)
          return
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault()
          spreadsheet.moveActiveCell(-1, 0, event.shiftKey)
          return
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault()
          spreadsheet.moveActiveCell(0, 1, event.shiftKey)
          return
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          spreadsheet.moveActiveCell(0, -1, event.shiftKey)
          return
        }

        if (event.key === 'Tab') {
          event.preventDefault()
          spreadsheet.moveActiveCell(0, event.shiftKey ? -1 : 1)
          return
        }

        if (event.key === 'Escape') {
          spreadsheet.cancelEdit()
          return
        }

        if (shouldClearSelectionOnKey(event.key)) {
          event.preventDefault()
          void spreadsheet.clearSelection()
          return
        }

        if (
          event.key.length === 1 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey
        ) {
          event.preventDefault()
          spreadsheet.beginEdit(event.key, { selectDraft: false })
        }
      }}
      onPaste={(event) => {
        if (event.target !== event.currentTarget) return

        event.preventDefault()
        void spreadsheet.pasteText(event.clipboardData.getData('text/plain'))
      }}
    >
      {isEditing ? (
        <input
          ref={setInputNode}
          value={spreadsheet.draftValue}
          onChange={(event) => spreadsheet.setDraftValue(event.target.value)}
          onKeyDown={(event) => {
            event.stopPropagation()

            if (event.key === 'Enter') {
              event.preventDefault()
              skipNextBlurCommitRef.current = true
              void spreadsheet.commitDraft()
              window.setTimeout(() => {
                skipNextBlurCommitRef.current = false
              }, 0)
            }

            if (event.key === 'Tab') {
              event.preventDefault()
              skipNextBlurCommitRef.current = true
              void spreadsheet.commitDraft()
              spreadsheet.moveActiveCell(0, event.shiftKey ? -1 : 1)
              window.setTimeout(() => {
                skipNextBlurCommitRef.current = false
              }, 0)
            }

            if (event.key === 'Escape') {
              event.preventDefault()
              skipNextBlurCommitRef.current = true
              spreadsheet.cancelEdit()
            }
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
          onPaste={(event) => event.stopPropagation()}
          onBlur={handleBlur}
          className="h-full w-full border-0 bg-transparent p-0 text-sm outline-none"
        />
      ) : previewValue !== null ? (
        <div className="min-w-0 flex-1 truncate">{previewValue}</div>
      ) : (
        <div className="min-w-0 flex-1">{children}</div>
      )}
    </div>
  )
}
